<template>
    <div class="rule-content">
        <h3 class="title">{{ $t('jisuzhajinhua.basicGameplay')}}</h3>
        <div class="list !pl-0">
            <p v-for="(paragraph, index) in basicParagraphs" :key="`bp-${index}`">{{ paragraph }}</p>
            <table class="table payout">
                <thead>
                    <tr>
                        <th>{{ $t('jisuzhajinhua.cardType') }}</th>
                        <th>{{ $t('jisuzhajinhua.multiplier') }}</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="(row, index) in payoutTable" :key="`pt-${index}`">
                        <td>{{ row[0] }}</td>
                        <td>{{ row[1] }}</td>
                    </tr>
                </tbody>
            </table>
        </div>
        <!-- 
            ===================
        -->
        <h3 class="title">
            {{ $t('goldenFlower.rule02Title') }}
        </h3>
        <table class="table">
            <thead>
                <tr>
                    <th>
                        {{ $t('goldenFlower.rule02_column01A') }}
                    </th>
                    <th>{{ $t('goldenFlower.rule02_column02A') }}</th>
                    <th >{{ $t('goldenFlower.rule02_column03A') }}</th>
                </tr>
            </thead>
            <tbody>
                <tr v-for="(it, idx) in ['B', 'C', 'D', 'E', 'F', 'G']" :key="it">
                    <td>
                        {{ $t(`goldenFlower.rule02_column01${it}`) }}
                    </td>
                    <td v-html="$t(`goldenFlower.rule02_column02${it}`)"></td>
                    <td class="w-[70px]">
                        <div class="relative flex items-center justify-center flex-nowrap">
                            <PokerCard
                                v-for="(val, i) in cardList[idx]"
                                :cardName="val"
                                :real="true"
                                class="absolute"
                                :style="{ left: `${15 * i}px` }"
                                :key="`${idx}-${i}`"
                            />
                        </div>
                    </td>
                </tr>
            </tbody>
        </table>
        <!-- 
            ===================
        -->
        <h3 class="title">
            {{ $t('goldenFlower.rule03Title') }}
        </h3>
        <ol class="list">
            <li
                class="break-all"
                v-for="i in [1, 2, 3]"
                :key="i"
                v-html="$t(`goldenFlower.rule03_${i}`)"
            ></li>
        </ol>
        <!-- 结算规则 -->
        <h3 class="title">
            {{ $t('jisuzhajinhua.settlementRules') }}
        </h3>
        <div class="list">
            <p><strong>{{ $t('jisuzhajinhua.settlementRoomMinBet') }}</strong></p>
            <p><strong>{{ $t('jisuzhajinhua.settlementMultiplier') }}</strong></p>
            <br>
            <p><strong>{{ $t('jisuzhajinhua.settlementLoserCalculation') }}</strong></p>
            <p>{{ $t('jisuzhajinhua.settlementLoserFormula') }}</p>
            <p><em>{{ $t('jisuzhajinhua.settlementLoserExample') }}</em></p>
            <br>
            <p><strong>{{ $t('jisuzhajinhua.settlementWinnerCalculation') }}</strong></p>
            <p>{{ $t('jisuzhajinhua.settlementWinnerFormula') }}</p>
            <p>{{ $t('jisuzhajinhua.settlementRakeInfo') }}</p>
            <br>
            <p><strong>{{ $t('jisuzhajinhua.settlementAntiMechanism') }}</strong></p>
            <p>{{ $t('jisuzhajinhua.settlementAntiDescription') }}</p>
            <p><em>{{ $t('jisuzhajinhua.settlementAntiExample') }}</em></p>
        </div>
        <h3 class="title">
            {{ $t('goldenFlower.rule05Title') }}
        </h3>
        <div
            class="list break-all"
            v-html="$t(`goldenFlower.rule05_1`)"
        ></div>
    </div>
</template>

<script lang="ts" setup>
// @ts-ignore
import PokerCard from '~/pages/casino/zhajinhuaGame/_components/pokerCard.vue';
import { useJoinStateStore } from '@/stores/cansino/joinState';
import { useI18n } from 'vue-i18n';

const joinStateStore = useJoinStateStore();
const { t, te, tm, locale } = useI18n();

// 读取多语言数组（带兜底）
const basicParagraphs = computed(() => {
    try {
        const paragraphs = tm('jisuzhajinhua.basicParagraphs') as unknown as string[];
        console.log('basicParagraphs from i18n:', paragraphs);
        if (paragraphs && Array.isArray(paragraphs) && paragraphs.length > 0) {
            return paragraphs;
        }
    } catch (error) {
        console.warn('Failed to get basicParagraphs from i18n:', error);
    }
    
    // 兜底内容
    if (locale.value === 'en') {
        return [
            'After entering the game, the system automatically starts the round and will auto-match the next round.',
            'If disconnected, it exits after this round (5 rounds). Clicking stop will stop after this round.',
            'The game stops after the current round ends.',
            'Then directly compare hand strengths.',
            'The player with the highest hand wins this round; others lose.',
            'Losers pay the winner according to the settlement rules.',
            'Payout multipliers by hand are as follows:',
        ];
    } else {
        return [
            '进入游戏后，系统自动开始对局并会自动匹配下局',
            '断开连接会在本轮（共5局）结束退出，点击停止',
            '游戏则在本局结束后停止',
            '然后直接开始比较大小',
            '牌型最大的玩家即为本局赢家，其他玩家为输家',
            '输家按结算规则向赢家赔付游戏币',
            '不同牌型对应赔付倍率如下表',
        ];
    }
});

const payoutTable = computed(() => {
    try {
        const table = tm('jisuzhajinhua.payoutTable') as unknown as string[][];
        if (table && Array.isArray(table) && table.length > 0) {
            return table;
        }
    } catch (error) {
        console.warn('Failed to get payoutTable from i18n:', error);
    }
    
    // 兜底内容
    if (locale.value === 'en') {
        return [
            ['Three of a Kind', '40'],
            ['Straight Flush', '20'],
            ['Flush', '10'],
            ['Straight', '6'],
            ['Pair', '4'],
            ['High Card', '2'],
        ];
    } else {
        return [
            ['豹子', '40'],
            ['顺金', '20'],
            ['金花', '10'],
            ['顺子', '6'],
            ['对子', '4'],
            ['单张', '2'],
        ];
    }
});

const cardList = [
    ['clubsA', 'heartsA', 'spadesA'],
    ['spadesA', 'spadesK', 'spadesQ'],
    ['spadesA', 'spadesK', 'spadesJ'],
    ['spadesA', 'spadesK', 'heartsQ'],
    ['spadesA', 'heartsA', 'spadesK'],
    ['spadesA', 'heartsK', 'spadesJ'],
];
</script>

<style lang="scss" scoped>
.rule-content {
    color: rgba(147, 172, 211, 1);
}

.title {
    color: white;
    font-family: Kanit;
    font-weight: 600;
    font-size: 18px;
    line-height: 20px;
    margin: 0 0 10px;
}

.list {
    margin: 0 0 15px;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    padding-left: 1.1em;
    list-style: decimal !important; // 显示数字序号
    li {
        list-style: decimal; // 显示数字序号
    }
}

/* payout 表格复用 .table 样式，无需单独样式 */

.table {
    width: 100%;
    text-align: center;
    // border-collapse: separate;
    // border-spacing: 0;
    overflow: hidden;
    border-radius: 10px;
    border-style: hidden; /* 消除掉外边框 */
    border-collapse: collapse; /* 消除单元格之间的空隙 */
    box-shadow: 0 0 0 1px rgba(42, 53, 70, 1); /* hack 一下加个阴影 */
    border: 1px solid rgba(42, 53, 70, 1);
    font-weight: 500;
    font-size: 12px;
    margin: 0 0 15px;

    thead {
        background-color: rgba(27, 38, 54, 1);
        color: white;
    }
    tr th:nth-child(1),
    tr td:nth-child(1) {
        background-color: rgba(28, 37, 50, 1);
        color: white;
    }
    th,
    td {
        min-width: calc(100% / 4);
        max-width: calc(100% / 3);
        border: solid 1px rgba(42, 53, 70, 1);
        padding: 1em 0.5em;
    }
    .unit {
        color: rgba(151, 164, 176, 1);
        font-size: 0.9em;
        font-weight: normal;
    }
}

/* 基础玩法的赔付表：左列自适应，右列固定宽度并使用等宽/表格数字 */
.table.payout {
    /* 牌型列更窄 */
    th:nth-child(1), td:nth-child(1) {
        width: 90px;
        max-width: 90px;
        text-align: left;
    }
    /* 倍数列自适应且数字等宽 */
    th:nth-child(2), td:nth-child(2) {
        text-align: center;
        font-variant-numeric: tabular-nums; /* 宽度一致的数字 */
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
    }
}
</style>


